import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SidebarItem from './SidebarItem';
import {
    Users,
    ShoppingBag,
    Package,
    Palette,
    BookType,
    ChessQueen,
    ClipboardList,
    Zap,
    ALargeSmall,
    SettingsIcon,
    Handshake,
    SlidersHorizontal,
    ListOrdered,
    Mail,
    Baby,
    X,
    Ticket,
    Megaphone,
    CircleDollarSign,
    MapPin,
} from 'lucide-react';
import { useBackendAuth } from '../../hooks/useAuth';

type View =
    | 'users'
    | 'orders'
    | 'products'
    | 'settings'
    | 'login-prompts'
    | 'marketplace-economics'
    | 'pickup-locations'
    | 'categories'
    | 'specifications'
    | 'sidebar-filters'
    | 'checklists'
    | 'partner-inquiries'
    | 'tickets'
    | 'status'
    | 'translations'
    | 'setup'
    | 'email-templates'
    | 'myrebaby-designer'
    | 'meta-ad-catalog';

interface AdminSidebarProps {
    currentView: View;
    onViewChange: (view: View) => void;
    /** Mobile drawer open state; ignored on lg+ */
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

/** Matches fixed AdminHeader h-16 */
const ADMIN_HEADER_PX = 64;

/**
 * Pin a fixed drawer to the *visible* iOS Safari viewport.
 * `bottom: 0` uses the layout viewport and sits under Safari's bottom chrome;
 * visualViewport height shrinks when that bar is shown.
 */
function applyVisualViewportBox(el: HTMLElement, headerPx: number) {
    const vv = window.visualViewport;
    if (!vv) {
        el.style.top = `${headerPx}px`;
        el.style.bottom = '0px';
        el.style.height = 'auto';
        return;
    }

    // Fixed coords are layout-viewport relative; vv.offsetTop accounts for URL-bar scroll.
    const top = vv.offsetTop + headerPx;
    const height = Math.max(160, vv.height - headerPx);
    el.style.top = `${Math.max(0, top)}px`;
    el.style.height = `${height}px`;
    el.style.bottom = 'auto';
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
    currentView,
    onViewChange,
    mobileOpen = false,
    onMobileClose,
}) => {
    const { user } = useBackendAuth();
    const welcomeEmail = user?.email?.trim() || user?.userName?.trim() || '';
    const mobileDrawerRef = useRef<HTMLDivElement | null>(null);

    const select = (view: View) => {
        onViewChange(view);
        onMobileClose?.();
    };

    // Prevent background page scroll while the mobile drawer is open.
    // Do not use body.scroll-locked (touch-action: none) — it can block drawer scrolling on iOS.
    useEffect(() => {
        if (!mobileOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileOpen]);

    // Keep mobile drawer inside the visual viewport (above Safari bottom bar / home indicator).
    useEffect(() => {
        if (!mobileOpen) return;
        const el = mobileDrawerRef.current;
        if (!el) return;

        const sync = () => applyVisualViewportBox(el, ADMIN_HEADER_PX);
        sync();

        const vv = window.visualViewport;
        vv?.addEventListener('resize', sync);
        vv?.addEventListener('scroll', sync);
        window.addEventListener('resize', sync);
        window.addEventListener('orientationchange', sync);

        // Extra passes after paint / Safari chrome animation
        const t1 = window.setTimeout(sync, 50);
        const t2 = window.setTimeout(sync, 300);

        return () => {
            vv?.removeEventListener('resize', sync);
            vv?.removeEventListener('scroll', sync);
            window.removeEventListener('resize', sync);
            window.removeEventListener('orientationchange', sync);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
    }, [mobileOpen]);

    const renderNavItems = () => (
        <>
            <SidebarItem
                icon={<Zap size={20} />}
                label="Status"
                isActive={currentView === 'status'}
                onClick={() => select('status')}
            />
            <SidebarItem
                icon={<SettingsIcon size={20} />}
                label="setup"
                isActive={currentView === 'setup'}
                onClick={() => select('setup')}
            />
            <SidebarItem
                icon={<ALargeSmall size={20} />}
                label="Translations"
                isActive={currentView === 'translations'}
                onClick={() => select('translations')}
            />
            <SidebarItem
                icon={<Mail size={20} />}
                label="Email templates"
                isActive={currentView === 'email-templates'}
                onClick={() => select('email-templates')}
            />
            <SidebarItem
                icon={<Baby size={20} />}
                label="My Journey designer"
                isActive={currentView === 'myrebaby-designer'}
                onClick={() => select('myrebaby-designer')}
            />
            <SidebarItem
                icon={<Megaphone size={20} />}
                label="Meta ad catalog"
                isActive={currentView === 'meta-ad-catalog'}
                onClick={() => select('meta-ad-catalog')}
            />
            <SidebarItem
                icon={<Users size={20} />}
                label="Users"
                isActive={currentView === 'users'}
                onClick={() => select('users')}
            />
            <SidebarItem
                icon={<ShoppingBag size={20} />}
                label="Orders"
                isActive={currentView === 'orders'}
                onClick={() => select('orders')}
            />
            <SidebarItem
                icon={<Package size={20} />}
                label="Products"
                isActive={currentView === 'products'}
                onClick={() => select('products')}
            />
            <SidebarItem
                icon={<Palette size={20} />}
                label="Website Settings"
                isActive={currentView === 'settings'}
                onClick={() => select('settings')}
            />
            <SidebarItem
                icon={<ListOrdered size={20} />}
                label="Login prompts"
                isActive={currentView === 'login-prompts'}
                onClick={() => select('login-prompts')}
            />
            <SidebarItem
                icon={<CircleDollarSign size={20} />}
                label="Marketplace economics"
                isActive={currentView === 'marketplace-economics'}
                onClick={() => select('marketplace-economics')}
            />
            <SidebarItem
                icon={<MapPin size={20} />}
                label="Pickup cities"
                isActive={currentView === 'pickup-locations'}
                onClick={() => select('pickup-locations')}
            />
            <SidebarItem
                icon={<BookType size={20} />}
                label="Product categories"
                isActive={currentView === 'categories'}
                onClick={() => select('categories')}
            />
            <SidebarItem
                icon={<ChessQueen size={20} />}
                label="Product specifications"
                isActive={currentView === 'specifications'}
                onClick={() => select('specifications')}
            />
            <SidebarItem
                icon={<SlidersHorizontal size={20} />}
                label="Sidebar Filters"
                isActive={currentView === 'sidebar-filters'}
                onClick={() => select('sidebar-filters')}
            />
            <SidebarItem
                icon={<ClipboardList size={20} />}
                label="Checklist Templates"
                isActive={currentView === 'checklists'}
                onClick={() => select('checklists')}
            />
            <SidebarItem
                icon={<Handshake size={20} />}
                label="Partner Inquiries"
                isActive={currentView === 'partner-inquiries'}
                onClick={() => select('partner-inquiries')}
            />
            <SidebarItem
                icon={<Ticket size={20} />}
                label="Support tickets"
                isActive={currentView === 'tickets'}
                onClick={() => select('tickets')}
            />
        </>
    );

    /*
      Mobile: portal to body.
      Do NOT use bottom:0 alone — on iPhone Safari that sits under the browser chrome.
      visualViewport sets height to the visible area so scroll ends above the bar.
    */
    const mobileDrawer =
        typeof document !== 'undefined'
            ? createPortal(
                  <>
                      {mobileOpen && (
                          <div
                              className="fixed inset-0 z-[55] bg-black/40 lg:hidden"
                              onClick={onMobileClose}
                              aria-hidden
                          />
                      )}
                      <div
                          ref={mobileDrawerRef}
                          className={`fixed left-0 z-[56] flex w-64 max-w-[85vw] flex-col overflow-hidden bg-white shadow-lg transition-transform duration-200 ease-out lg:hidden ${
                              mobileOpen ? 'translate-x-0' : 'pointer-events-none -translate-x-full'
                          }`}
                          // Fallback before visualViewport effect runs
                          style={{
                              top: ADMIN_HEADER_PX,
                              bottom: 0,
                          }}
                          role="dialog"
                          aria-modal={mobileOpen}
                          aria-label="Admin menu"
                          aria-hidden={!mobileOpen}
                      >
                          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 bg-white px-4 py-3">
                              <div className="min-w-0">
                                  <h2 className="text-lg font-bold text-gray-800">Admin Panel</h2>
                                  {welcomeEmail && (
                                      <p
                                          className="mt-0.5 truncate text-xs text-gray-500"
                                          title={welcomeEmail}
                                      >
                                          {welcomeEmail}
                                      </p>
                                  )}
                              </div>
                              <button
                                  type="button"
                                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
                                  onClick={onMobileClose}
                                  aria-label="Close menu"
                              >
                                  <X size={22} />
                              </button>
                          </div>
                          <nav
                              className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 pt-3"
                              style={{
                                  WebkitOverflowScrolling: 'touch',
                                  // Home indicator + room under last row (Safari chrome handled by visualViewport height)
                                  paddingBottom:
                                      'calc(1.25rem + env(safe-area-inset-bottom, 0px) + 1.5rem)',
                              }}
                              aria-label="Admin sections"
                          >
                              {renderNavItems()}
                              {/* Spacer so last tap targets clear any residual chrome */}
                              <div className="h-10 shrink-0" aria-hidden />
                          </nav>
                      </div>
                  </>,
                  document.body
              )
            : null;

    // Desktop: fixed column under the header
    const desktopSidebar = (
        <div className="fixed bottom-0 left-0 top-16 z-40 hidden w-64 flex-col bg-white shadow-lg lg:flex">
            <div className="shrink-0 px-6 pb-2 pt-6">
                <h2 className="text-2xl font-bold text-gray-800">Admin Panel</h2>
                {welcomeEmail && (
                    <p className="mt-2 break-all text-sm text-gray-500" title={welcomeEmail}>
                        Welcome ({welcomeEmail})
                    </p>
                )}
            </div>
            <nav
                className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-6 pb-8 pt-2"
                style={{ WebkitOverflowScrolling: 'touch' }}
                aria-label="Admin sections"
            >
                {renderNavItems()}
            </nav>
        </div>
    );

    return (
        <>
            {mobileDrawer}
            {desktopSidebar}
        </>
    );
};

export default AdminSidebar;
